import type { AuthenticatedUser } from './auth'

export type Overview = {
  summary: {
    teams: number
    api_keys: number
    sandboxes: number
    running_sandboxes: number
    paused_sandboxes: number
    snapshots: number
    volumes: number
  }
  statusRows: Array<{ status: string; count: number }>
  recentSandboxes: Array<{
    id: string
    team_id: string
    template_id: string | null
    status: string
    updated_at: string
  }>
  recentSnapshots: Array<{
    build_id: string
    sandbox_id: string
    team_id: string
    snapshot_state: string
    created_at: string
  }>
}

export type Team = {
  team_id: string
  api_keys: number
  sandboxes: number
  running: number
  paused: number
  killed: number
  snapshots: number
  volumes: number
  last_activity_at: string | null
}

export type TeamDetail = {
  teamId: string
  keys: Array<{
    id: string
    name: string
    api_key_prefix: string
    api_key_mask: string | null
    created_at: string
    is_fixed: boolean
    last_used_at: string | null
  }>
  sandboxes: Array<{
    id: string
    template_id: string | null
    status: string
    timeout_seconds: number | null
    webhook_url: string | null
    created_at: string
    updated_at: string
    last_active_at: string | null
  }>
  snapshots: Array<{
    build_id: string
    sandbox_id: string
    template_id: string | null
    base_template_id: string | null
    snapshot_state: string
    created_at: string
    paused_at: string | null
    error_message: string | null
  }>
  volumes: Array<{
    id: string
    name: string
    volume_type: string
    created_at: string
  }>
}

export type Template = {
  id: string
  name: string
  bucket: string
  description: string
  default: boolean
  status?: string
  storageUri?: string
  memoryMb?: number
  rootfsSizeMb?: number
  artifactSizeBytes?: number
  createdAt?: string
  metadata?: Record<string, unknown>
}

export type DiskCacheMetrics = {
  path: string
  totalGb: number | null
  availableGb: number | null
  usedGb: number | null
  usedPercent: number | null
  mounted: boolean
  unlimited: boolean
}

export type OrchestratorNode = {
  id: string
  address: string
  healthy: boolean
  error: string | null
  metrics: {
    sandboxCount: number | null
    cpuCount: number | null
    cpuAllocated: number | null
    cpuPercent: number | null
    memoryTotalGb: number | null
    memoryUsedGb: number | null
    memoryAllocatedGb: number | null
    memoryAvailableGb: number | null
    memoryConsumedGb: number | null
    localCache: DiskCacheMetrics
    nfsCache: DiskCacheMetrics
    uptime: string | null
    commit: string | null
    buildTime: string | null
    machineInfo: Record<string, unknown> | null
  } | null
  status: {
    totalVms: number | null
    fcProcessCount: number | null
    leakDetected: boolean
  } | null
}

export type Orchestrators = {
  gatewayUrl: string
  gatewayHttpStatus: number
  gatewayStatus: string
  healthyCount: number
  totalCount: number
  orchestrators: OrchestratorNode[]
}

export type ClusterConfig = {
  sandboxGatewayUrl: string
  sandboxMasterKey: string | null
  sandboxMasterKeyMask: string | null
}

export type ProvisionTeamResult = {
  teamId: string
  apiKeyId: string
  apiKeySecret: string
  prefix: string
  mask: string
}

export type CreateSandboxResult = {
  sandboxID: string
  templateID: string
  domain: string
  state: string
  startedAt?: string
}

export async function api<T>(user: AuthenticatedUser, path: string): Promise<T> {
  return apiRequest<T>(user, path)
}

export async function apiRequest<T>(
  user: AuthenticatedUser,
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = await user.getIdToken()
  const response = await fetch(path, {
    ...options,
    headers: {
      ...(options.body ? { 'content-type': 'application/json' } : {}),
      authorization: `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const message = await response.text()
    throw new Error(message || `Request failed: ${response.status}`)
  }

  return (await response.json()) as T
}
